import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { AssignCustomFieldDropdown } from './AssignCustomFieldDropdown';
import type { CustomFieldsLevel } from 'hooks/useCustomFieldRules';
import { supportUrls } from 'utils/urls';
import { AssignedCustomFields } from './AssignedCustomFields';
import type { AssignedCustomFieldsQueryOptions, UnassignedCustomFieldsQueryOptions } from 'services/custom-fields/customFieldsQueries';
import { DesignTokenProvider, designToken } from '@proposify/ui';
import { autocompleteClasses } from '@mui/material/Autocomplete';
import type { CustomField } from 'services/custom-fields/schemas';
import { AssignedCustomFieldsErrorBoundary } from './AssignedCustomFieldsErrorBoundary';

type AssignCustomFieldsDialogProps = {
  open: boolean;
  onClose: () => void;
  level: CustomFieldsLevel;
  listAssignedQuery: AssignedCustomFieldsQueryOptions;
  listUnassignedQuery: UnassignedCustomFieldsQueryOptions;
  onAssign: (field: CustomField) => void;
  onDelete?: (customFieldId: string) => void;
};

export function AssignCustomFieldsDialog({
  open,
  onClose,
  level,
  listAssignedQuery,
  listUnassignedQuery,
  onAssign,
  onDelete,
}: AssignCustomFieldsDialogProps) {
  const { t } = useTranslation();

  return (
    <DesignTokenProvider theme={designToken}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        aria-labelledby="assign-custom-fields-dialog-title"
        aria-describedby="assign-custom-fields-dialog-subtitle"
        data-dd-action-name="Custom Fields Zone"
      >
        <DialogTitle id="assign-custom-fields-dialog-title" sx={{ m: 0, px: 3, pt: 3, pb: 1 }}>
          <Typography variant="h6" component="span" fontWeight={600}>
            {t('editor.variables.manage_custom_fields_title', { level })}
          </Typography>
          <Typography id="assign-custom-fields-dialog-subtitle" variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('editor.variables.manage_custom_fields_subtitle', { level })}&nbsp;
            <Link
              href={supportUrls.get(supportUrls.customFields)}
              target="_blank"
              rel="noopener noreferrer"
              underline="always"
              color="inherit"
            >
              {t('editor.variables.learn_more', 'Learn More')}
            </Link>
          </Typography>
          <IconButton
            data-dd-action-name="Close custom fields dialog"
            aria-label={t('close_dialog', 'Close dialog')}
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 16,
              top: 16,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            height: '450px',
            padding: '1 3 3 3',
            display: 'flex',
            flexDirection: 'column',
            [`& .${autocompleteClasses.root}`]: {
              width: '50%',
            },
          }}
        >
          <AssignedCustomFieldsErrorBoundary>
            <AssignCustomFieldDropdown onAssign={onAssign} listUnassignedQuery={listUnassignedQuery} />
            <AssignedCustomFields level={level} query={listAssignedQuery} onDelete={onDelete} />
          </AssignedCustomFieldsErrorBoundary>
        </DialogContent>
      </Dialog>
    </DesignTokenProvider>
  );
}
